import { FormFieldType } from '../../../@types/form-builder.type';

/**
 * Field types whose control draws its own visible label, so the surrounding
 * label wrapper (`InteractionFieldBlock` on mobile, `SectionLabel` on desktop)
 * must not add a second one. Rendering the same label twice is called out in
 * coding-guidelines.md §2.9.
 *
 * This is the surface-independent core: every type here is either handled by a
 * shared `Interaction*Field` that renders its own title, or renders nothing at
 * all. Surfaces add their own extras — a desktop `RadioGroup`/`Presentation`
 * self-labels while the mobile equivalents do not — see
 * `DESKTOP_SELF_LABELLING_FIELD_TYPES` below.
 */
export const SELF_LABELLING_FIELD_TYPES: ReadonlySet<string> = new Set<string>([
  FormFieldType.Checkbox,
  FormFieldType.Label,
  FormFieldType.SetParameter,
  FormFieldType.GetParameter,
  FormFieldType.Block,
  FormFieldType.LabourTime,
  FormFieldType.FaultSelection,
  FormFieldType.SolutionAction,
  FormFieldType.PartsRequest,
  FormFieldType.Signature,
  FormFieldType.GetGeoLocation,
]);

/**
 * The desktop set. Adds the types whose desktop atoms carry a label the mobile
 * equivalents don't:
 *
 * - `RadioGroup` takes a `title`, and Presentation is rendered as its own title
 *   text rather than a titled select.
 * - The upload types go through `UploadButton`, which is given `label={field.title}`.
 *   The shared `InteractionFileUploadField` that mobile uses only ever says
 *   "Choose File", so on mobile those types must keep the wrapper's label — without
 *   it a technician sees an upload button with no idea what it is for.
 */
export const DESKTOP_SELF_LABELLING_FIELD_TYPES: ReadonlySet<string> = new Set<string>([
  ...SELF_LABELLING_FIELD_TYPES,
  FormFieldType.Radio,
  FormFieldType.Presentation,
  FormFieldType.File,
  FormFieldType.Photo,
  FormFieldType.MerchantDamageTemplate,
]);
